/**
 * Which workspace of the current invoice world is on screen? (Rechnungen 2.0 — R6b)
 *
 * TWO PIECES OF URL STATE, DELIBERATELY ORTHOGONAL. `?source=` answers "which of
 * the two invoice worlds am I reading"; `?view=` answers "which workspace of it
 * am I looking at". Every combination of the two is meaningful — Bexio invoices,
 * Bexio payments, the Vantio history, and the Vantio history's answer that it
 * records no individual payments — so neither parameter may reset the other.
 * That rule lives entirely in the setter: it copies the search parameters it was
 * given and writes exactly one key, the same discipline `setSource` follows.
 *
 * EXACT AND CASE-SENSITIVE, for the reason `normalizeInvoiceSource` is. A URL is
 * written by a person or by our own code, and neither produces `"Payments"` by
 * accident, so accepting it would only mean guessing at intent. There is no
 * combined workspace either: the two answer different questions and a page
 * holding both would be the mount-all defect this slice exists to avoid.
 *
 * AN UNUSABLE VALUE FALLS BACK WITHOUT REWRITING THE URL. The address bar keeps
 * what its sender wrote. Cleaning it would add a history entry nobody asked for
 * and would hide from that sender that their link named something this page does
 * not have.
 *
 * IT READS A SEARCH PARAMETER AND NOTHING ELSE. No request, no storage, no
 * derived data — the URL is state here, never a data source.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** The only two workspaces. There is no combined value. */
export const INVOICE_VIEWS = ["invoices", "payments"];

/** The search parameter the choice travels in. */
export const INVOICE_VIEW_PARAM = "view";

/** The workspace shown when nothing usable was asked for. */
export const DEFAULT_INVOICE_VIEW = "invoices";

/** A raw `?view=` value, or `null` if it is not one of the two. */
export function normalizeInvoiceView(raw) {
  return INVOICE_VIEWS.includes(raw) ? raw : null;
}

/**
 * Which workspace to show.
 *
 * Unlike the source, this never returns `null`: the invoice list needs no
 * capability read and no provider to be a truthful answer, so there is no state
 * in which the page has nothing to render.
 */
export function resolveInvoiceView(requestedView) {
  return normalizeInvoiceView(requestedView) ?? DEFAULT_INVOICE_VIEW;
}

export default function useInvoiceView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedView = searchParams.get(INVOICE_VIEW_PARAM);
  const view = useMemo(() => resolveInvoiceView(requestedView), [requestedView]);

  const setView = useCallback(
    (next) => {
      const value = normalizeInvoiceView(next);
      // A value the contract rejects is never written. Nothing in the product
      // produces one; this guards a future caller rather than a reachable case.
      if (value === null) return;
      setSearchParams((prev) => {
        // Copied so `source` — and everything else — survives the switch.
        const params = new URLSearchParams(prev);
        params.set(INVOICE_VIEW_PARAM, value);
        return params;
      });
      // Default push semantics: back and forward step through the workspaces
      // the reader actually visited.
    },
    [setSearchParams]
  );

  return {
    view,
    setView,
    isInvoicesView: view === "invoices",
    isPaymentsView: view === "payments",
  };
}
